/**
 * useToken Hook
 *
 * AWS Cognito JWT token management and validation hook.
 * Handles dual token storage (ID token + Access token), expiry validation,
 * and automatic logout on token expiration.
 *
 * Security architecture:
 * - Dual token storage: ID token (user claims) + Access token (API authorization)
 * - 60-second expiry buffer for clock skew between client and Cognito servers
 * - Client-side validation with server-side verification on sensitive operations
 * - SSO cookie cleanup on logout to prevent session leakage
 *
 * WHY dual token storage:
 * - ID token contains user claims (name, email, groups) for UI rendering
 * - Access token used for API authorization (shorter lived, more secure)
 * - Cognito best practice: separate identity from authorization
 *
 * WHY 60-second expiry buffer:
 * - Prevents race conditions where token expires between validation and API call
 * - Accounts for client/server clock drift (NTP synchronization issues)
 * - Reduces failed API calls due to just-expired tokens
 *
 * @returns {Object} Token utilities and validation functions
 *
 * @see src/services/apiAuth.js - Cognito authentication service
 * @see https://docs.aws.amazon.com/cognito/latest/developerguide/amazon-cognito-user-pools-using-tokens-with-identity-providers.html
 */

import { useEffect, useState, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

/**
 * JWT token payload interface
 *
 * WHY typed payload:
 * - Type safety for token claims (email, sub, groups)
 * - Prevents accessing undefined claims
 * - Documents expected token structure from Cognito
 */
export interface JwtPayload {
  /** Token expiration timestamp (Unix epoch seconds) */
  exp: number;

  /** Token issued at timestamp (Unix epoch seconds) */
  iat: number;

  /** Cognito user sub (UUID) - stable user identifier */
  sub: string;

  /** Cognito username (email or unique identifier) */
  username?: string;

  /** User email address */
  email?: string;

  /** Email verified flag */
  email_verified?: boolean;

  /** User's full name */
  name?: string;

  /** Cognito user groups (roles) */
  'cognito:groups'?: string[];

  /** Token audience (client app ID) */
  aud?: string;

  /** Token issuer (Cognito user pool) */
  iss?: string;

  /** Token type (Bearer) */
  token_use?: string;

  /** Custom claims from Cognito */
  [key: string]: unknown;
}

/**
 * Token storage keys in localStorage
 *
 * WHY these specific keys:
 * - Simple keys used by original implementation
 * - Easier debugging (can inspect in DevTools Application tab)
 * - Backward compatible with existing stored tokens
 */
const TOKEN_KEYS = {
  ID_TOKEN: 'idToken',
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
} as const;

/**
 * Token expiry buffer in seconds
 *
 * WHY 60 seconds:
 * - Clock skew tolerance: Client/server clocks may differ by 30-60s
 * - API call buffer: Prevents token expiring mid-request
 * - User experience: Smooth token refresh without visible interruption
 */
const EXPIRY_BUFFER_SECONDS = 60;

/**
 * UseToken result interface
 */
export interface UseTokenResult {
  /** ID token (user claims) */
  idToken: string | null;
  /** Access token (API authorization) */
  accessToken: string | null;
  /** Refresh token (long-lived) */
  refreshToken: string | null;
  /** Whether session is valid (tokens not expired) */
  sessionValid: boolean;
  /** Decoded ID token payload */
  decodedToken: JwtPayload | null;
  /** Store ID token */
  setIdToken: (token: string) => void;
  /** Get ID token from storage */
  getIdToken: () => string | null;
  /** Store access token */
  setAccessToken: (token: string) => void;
  /** Get access token from storage */
  getAccessToken: () => string | null;
  /** Set session validity manually */
  setSessionValid: (valid: boolean) => void;
  /** Store new tokens in localStorage (convenience method) */
  setTokens: (idToken: string, accessToken: string, refreshToken?: string) => void;
  /** Clear all tokens and logout */
  clearTokens: () => void;
  /** Check and update session validity */
  checkSessionValid: (token?: string) => void;
  /** Decode access token */
  decodeToken: () => JwtPayload | null;
  /** Decode ID token */
  decodeIdToken: () => JwtPayload | null;
  /** Get user claims from ID token */
  getUserClaims: () => JwtPayload | null;
}

/**
 * Token management hook
 *
 * Token lifecycle:
 * 1. Login → Store ID, Access, and Refresh tokens
 * 2. API calls → Use Access token in Authorization header
 * 3. UI rendering → Use ID token claims for user info
 * 4. Token expiry → Automatically refresh using Refresh token
 * 5. Logout → Clear all tokens and SSO cookies
 *
 * WHY not use Cognito SDK directly:
 * - SDK is 400KB+ and has dependency issues with Vite
 * - Manual token management gives finer control over storage
 * - Easier to debug token-related issues
 * - Better performance (no SDK initialization overhead)
 *
 * @example
 * // Basic token validation
 * const { isValid, idToken } = useToken();
 * if (!isValid) {
 *   // Redirect to login
 * }
 *
 * @example
 * // Get user claims
 * const { getUserClaims } = useToken();
 * const claims = getUserClaims();
 * console.log(claims.email, claims['cognito:groups']);
 *
 * @example
 * // Store tokens after login
 * const { setTokens } = useToken();
 * setTokens(idToken, accessToken, refreshToken);
 */
export function useToken(): UseTokenResult {
  // Token state
  const [sessionValid, setSessionValid] = useState<boolean>(false);
  const [idToken, setIdTokenState] = useState<string | null>(null);
  const [accessToken, setAccessTokenState] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<JwtPayload | null>(null);

  // WHY helper for safe decoding:
  // Keeps try/catch in one place so we don't repeat error handling every time we need claims.
  const decodePayload = (token: string | null): JwtPayload | null => {
    if (!token) return null;
    try {
      return jwtDecode<JwtPayload>(token);
    } catch {
      return null;
    }
  };

  /**
   * Store ID token in state and localStorage
   */
  function setIdToken(token: string): void {
    // WHY dual storage (state + localStorage):
    // - State: instant React reactivity for nav/menu updates
    // - Storage: survive hard refreshes + multi-tab sessions
    setIdTokenState(token);
    localStorage.setItem(TOKEN_KEYS.ID_TOKEN, token);
    setDecodedToken(decodePayload(token));
  }

  /**
   * Get ID token from localStorage
   */
  function getIdToken(): string | null {
    // WHY direct storage read:
    // Tokens can rotate in other tabs; reading from storage avoids stale state.
    return localStorage.getItem(TOKEN_KEYS.ID_TOKEN);
  }

  /**
   * Store access token in state and localStorage
   */
  function setAccessToken(token: string): void {
    // WHY keep setter separate:
    // API calls only need access token. Keeping dedicated setter maintains API parity with JS version.
    setAccessTokenState(token);
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, token);
  }

  /**
   * Get access token from localStorage
   */
  function getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
  }

  /**
   * Check and update session validity based on token expiry
   */
  function checkSessionValid(newToken?: string): void {
    const token = newToken || getAccessToken();

    if (!token) {
      setSessionValid(false);
      return;
    }

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const dateNow = Date.now() + EXPIRY_BUFFER_SECONDS * 1000;
      const isExpired = decoded.exp * 1000 < dateNow;

      setSessionValid(!isExpired);

      if (isExpired) {
        clearTokens();
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      setSessionValid(false);
    }
  }

  /**
   * Decode access token
   */
  function decodeToken(): JwtPayload | null {
    const token = getAccessToken();
    if (!token) return null;
    try {
      return jwtDecode<JwtPayload>(token);
    } catch {
      return null;
    }
  }

  /**
   * Decode ID token
   */
  function decodeIdToken(): JwtPayload | null {
    const token = getIdToken();
    if (!token) return null;
    try {
      return jwtDecode<JwtPayload>(token);
    } catch {
      return null;
    }
  }

  /**
   * Check session validity on mount
   */
  useEffect(() => {
    // WHY hydrate from storage on mount:
    // Prevents flash-of-logged-out, supports direct deep links, and matches legacy behaviour.
    const storedIdToken = localStorage.getItem(TOKEN_KEYS.ID_TOKEN);
    const storedAccessToken = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
    const storedRefreshToken = localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);

    if (storedIdToken) {
      setIdTokenState(storedIdToken);
      setDecodedToken(decodePayload(storedIdToken));
    }
    if (storedAccessToken) {
      setAccessTokenState(storedAccessToken);
    }
    if (storedRefreshToken) {
      setRefreshToken(storedRefreshToken);
    }

    checkSessionValid(storedAccessToken ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Convenience method to set both tokens at once
   */
  function setTokens(newIdToken: string, newAccessToken: string, newRefreshToken?: string): void {
    setIdToken(newIdToken);
    setAccessToken(newAccessToken);
    if (newRefreshToken) {
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, newRefreshToken);
      setRefreshToken(newRefreshToken);
    }
    checkSessionValid(newAccessToken);
  }

  /**
   * Clear all tokens and SSO cookies
   *
   * WHY clear SSO cookies:
   * - Prevents automatic re-login after explicit logout
   * - Required for proper SSO logout flow
   * - Removes stale session data
   *
   * WHY CHECK FOR SSO COOKIE NAME:
   * - Not all users have SSO enabled
   * - Only users authenticated through certain flows have the custom claim
   * - Checking prevents errors and unnecessary cookie operations
   *
   * HARDCODED DOMAIN (.bcfc.co.uk):
   * - Bristol City FC specific - should be dynamic in production
   * - Technical debt from initial single-tenant implementation
   *
   * Called on:
   * - User clicks logout
   * - Token validation fails (expired/invalid)
   * - Server returns 401 Unauthorized
   */
  function clearTokens(): void {
    // Check for SSO cookie in ID token claims
    const idTokenDecoded = decodeIdToken();

    if (idTokenDecoded && idTokenDecoded['custom:sso_cookie_name']) {
      const cookieName = idTokenDecoded['custom:sso_cookie_name'] as string;
      // WHY EPOCH DATE: Setting cookie to past date is standard way to delete
      // WHY domain=.bcfc.co.uk: Ensures we clear the exact SSO cookie
      // TODO(DEV-xxxx): SSO cookie domain is hardcoded to .bcfc.co.uk — must be
      // dynamic per tenant for multi-tenant SSO cleanup. Create a Linear issue.
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;domain=.bcfc.co.uk`;
    }

    // WHY SET NULL BEFORE REMOVE:
    // Setting state to null triggers immediate React updates (logged-out UI)
    // RemoveItem is for persistence. Order matters: UI update first, then storage cleanup.
    setIdTokenState(null);
    setAccessTokenState(null);
    setRefreshToken(null);
    setDecodedToken(null);
    setSessionValid(false);

    // Clear localStorage
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.ID_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get user claims from ID token
   *
   * WHY separate function:
   * - Convenient access to user info without decoding manually
   * - Type-safe access to token claims
   * - Null-safe return prevents undefined errors
   *
   * @returns Decoded token payload or null
   */
  const getUserClaims = useCallback((): JwtPayload | null => {
    return decodedToken;
  }, [decodedToken]);

  return {
    idToken,
    accessToken,
    refreshToken,
    sessionValid,
    decodedToken,
    setIdToken,
    getIdToken,
    setAccessToken,
    getAccessToken,
    setSessionValid,
    setTokens,
    clearTokens,
    checkSessionValid,
    decodeToken,
    decodeIdToken,
    getUserClaims,
  };
}
